import { Stack, styled, Typography } from '@mui/material';

/**
 * Product row container
 * Displays product information in a row layout
 * Uses #EEEFEF border color from Figma for all sizes
 */
export const ProductRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: theme.spacing(3),
  paddingTop: theme.spacing(3),
  paddingBottom: theme.spacing(3),
}));

/**
 * Product information container
 * Groups product fields horizontally
 * Mobile: Stacks vertically with Program on top
 */
export const ProductInfo = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  flex: 1,
  gap: theme.spacing(6),
  alignItems: 'center',
  justifyContent: 'space-between',
  marginRight: theme.spacing(10),
  [theme.breakpoints.down('lg')]: {
    marginRight: 0,
  },
}));

/**
 * Product field container
 * Displays label and value vertically
 */
export const ProductField = styled(Stack)(({ theme }) => ({
  flexDirection: 'column',
  gap: theme.spacing(0.5),
}));

/**
 * Price information container
 * Groups price and discount fields
 * Mobile: Stacks vertically
 */
export const ProductPriceInfo = styled(Stack)(() => ({
  flexDirection: 'column',
  alignItems: 'flex-end',
}));

/**
 * Footer info container
 * Groups label and value horizontally
 */
export const FooterInfo = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  gap: theme.spacing(1),
  alignItems: 'baseline',
}));

/**
 * Payment breakdown container - column layout
 * Used for mobile/tablet views
 */
export const PaymentBreakdownColumn = styled(Stack)(({ theme }) => ({
  flexDirection: 'column',
  gap: theme.spacing(2),
  paddingTop: theme.spacing(3),
  borderTop: `1px solid ${theme.palette.text.fourth}`,
  [theme.breakpoints.down('lg')]: {
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
}));

/**
 * Payment breakdown container - row layout
 * Used for desktop views
 */
export const PaymentBreakdownRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  justifyContent: 'space-between',
  paddingTop: theme.spacing(4.5),
  borderTop: `1px solid ${theme.palette.text.fourth}`,
}));

/**
 * Payment breakdown title
 * Body1/Regular, 16px, text.secondary color
 */
export const PaymentBreakdownTitle = styled(Typography)(({ theme }) => ({
  ...theme.typography.body1,
  color: theme.palette.text.secondary,
  fontSize: theme.typography.pxToRem(16),
  fontWeight: theme.typography.fontWeightRegular,
}));

/**
 * Payment breakdown info banner
 * Background rgba(132,88,252,0.2), border radius 18px, Body2/Regular 14px
 */
export const PaymentBreakdownInfo = styled(Stack)<{
  variant?: 'horizontal' | 'vertical';
}>(({ theme, variant = 'vertical' }) => ({
  backgroundColor: theme.palette.background.button,
  padding: variant === 'horizontal' ? theme.spacing(0, 3) : theme.spacing(2, 3),
  borderRadius: theme.spacing(4.5), // 18px
  [theme.breakpoints.up('xl')]: {
    marginRight: theme.spacing(6),
  },
}));

export const CreditsField = styled(Stack)(({ theme }) => ({
  flexDirection: 'column',
  gap: theme.spacing(0.5),
  flexShrink: 0,
  [theme.breakpoints.down('md')]: {
    width: '100%',
  },
}));

/**
 * Credits value display with icon
 * Shows credits amount with HubCredit icon
 */
export const CreditsValue = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  gap: theme.spacing(1.5),
  alignItems: 'center',
  '& .MuiSvgIcon-root': {
    fontSize: theme.spacing(4), // 16px
  },
  '& p': {
    ...theme.typography.body1,
    fontSize: theme.typography.pxToRem(16),
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.text.primary,
  },
}));

/**
 * Additional payment field container
 * Shows remaining payment amount with configurable alignment
 */
export const AdditionalPaymentField = styled(Stack)<{
  align?: 'left' | 'right';
}>(({ theme, align = 'right' }) => ({
  flexDirection: 'column',
  gap: theme.spacing(0.5),
  width: theme.spacing(45),
  textAlign: align,
  [theme.breakpoints.down('md')]: {
    width: '100%',
    paddingRight: 0,
  },
}));

/**
 * Additional payment value display
 * Shows remaining payment amount
 */
export const AdditionalPaymentValue = styled(Typography)(({ theme }) => ({
  ...theme.typography.body1,
  fontSize: theme.typography.pxToRem(16),
  fontWeight: theme.typography.fontWeightMedium,
  color: theme.palette.text.primary,
}));

/**
 * Trading system row container
 * Displays trading system information in a row layout
 */
export const TradingSystemRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  paddingTop: theme.spacing(3),
  paddingBottom: theme.spacing(3),
  borderTop: `1px solid ${theme.palette.text.fifth}`,
}));

export const TradingSystemPriceInfo = styled(Stack)(({ theme }) => ({
  flexDirection: 'column',
  alignItems: 'flex-start',
  [theme.breakpoints.down('lg')]: {
    alignItems: 'flex-end',
    justifyContent: 'space-between',
  },
}));

/**
 * Container for payment breakdown fields
 * Mobile: Column layout (credits/discount row, then additional payment row)
 * Desktop: Row layout (all fields in one row)
 */
export const FieldsContainer = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  gap: theme.spacing(10),
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
}));

/**
 * Container for credits and discount fields
 * Desktop: Row layout
 * Mobile: Column layout
 */
export const CreditsDiscountRow = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  gap: theme.spacing(10),
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
}));
